'use client';

import { useEffect, useRef } from 'react';

/**
 * TetherBeam — V4 horizontal spark that visually connects the "Talk to Bri"
 * FAB to the phone on the right-hand column.
 *
 * The beam's right end must terminate EXACTLY at the visual center of the
 * phone's Tap-to-Talk ring (`.hpd-ring-mount`), so instead of the historical
 * magic-number CSS (top: 53% / width: 32.5% — tuned for 1440px only) a tiny
 * measurement effect reads the ring mount's rendered rect and publishes it as
 * `--hv-tether-top` / `--hv-tether-width` custom properties on the beam;
 * hero-v4.css consumes them with the old percentages kept as fallbacks.
 * Re-measures on window resize and via ResizeObserver on both the hero
 * container (layout/font reflow) and the ring mount itself, so the endpoint
 * stays centered across breakpoints and phone-size changes.
 *
 * Auto-hidden below 1100px (grid collapses) — measurement is skipped while
 * hidden and re-runs on the breakpoint flip. When the FAB is hovered, CSS
 * `:has()` in hero-v4.css amplifies the beam (brighter, taller, faster spark
 * cycle). The `.hv-tether-impact` dot marks where the beam plugs into the
 * ring.
 *
 * See docs/design/v4-landing-hero-spec.md §8.
 */

const RING_MOUNT_SELECTOR = '[data-hero-v4] .hpd-ring-mount';

export function TetherBeam() {
  const beamRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const beam = beamRef.current;
    if (!beam) return;
    // Positioning context: the hero's `relative` layout container (the beam's
    // direct parent in Hero.tsx) — percentages/px on .hv-tether resolve
    // against it.
    const host = beam.parentElement;
    if (!host) return;
    const ring = document.querySelector<HTMLElement>(RING_MOUNT_SELECTOR);
    if (!ring) return;

    let frame: number | null = null;

    const measure = () => {
      frame = null;
      const hostRect = host.getBoundingClientRect();
      const ringRect = ring.getBoundingClientRect();
      if (hostRect.width === 0 || ringRect.width === 0) return;

      const centerX = ringRect.left + ringRect.width / 2 - hostRect.left;
      const centerY = ringRect.top + ringRect.height / 2 - hostRect.top;
      // The beam launches from the container's midline (past the copy
      // column, matching the historical left: 50%) and lands on the ring
      // center. If layout ever puts the ring left of the midline (stacked
      // mobile — beam is display:none there anyway), keep the CSS fallbacks.
      const startX = hostRect.width * 0.5;
      const width = centerX - startX;
      if (width <= 0) return;

      beam.style.setProperty('--hv-tether-top', `${centerY}px`);
      beam.style.setProperty('--hv-tether-width', `${width}px`);
    };

    const scheduleMeasure = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('resize', scheduleMeasure);

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(scheduleMeasure);
      observer.observe(host);
      observer.observe(ring);
    }

    return () => {
      window.removeEventListener('resize', scheduleMeasure);
      observer?.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div ref={beamRef} className="hv-tether" aria-hidden="true">
      <span className="hv-tether-spark" />
      <span className="hv-tether-impact" />
    </div>
  );
}

export default TetherBeam;
